import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Tab } from 'semantic-ui-react';
import { Document, UpsertLanguageDto } from 'ts-types/api.types';
import styled from 'styled-components';
import { addLanguage, editLanguage } from 'services/languageServices';
import Input from 'components/final-form/Input';
import TextArea from 'components/final-form/TextArea';
import { required } from 'utils/validatorUtils';
import CheckBox from 'components/final-form/CheckBox';
import { FormApi } from 'final-form';
import axios, { CancelTokenSource } from 'axios';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

const StyledTabPane = styled(Tab.Pane)`
  border: none !important;
  box-shadow: none !important;
`;

const StyledField = styled(Field)`
  margin-bottom: 10px !important;
`;

let initialDocumentTranslations: Array<Document> = [];

for (let i = 0; i < 4; i++) {
  initialDocumentTranslations.push({
    //@ts-ignore
    id: null,
    documentTypeId: i,
    //@ts-ignore
    languageId: null,
    text: "",
    deleted: 0
  });
}

const initialLanguageValues: UpsertLanguageDto = {
  description: "",
  basisLanguage: false,
  documentTranslations: initialDocumentTranslations
};

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  languageId?: number,
  language?: UpsertLanguageDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertBillTextForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }

  }

  handleSubmit = async (values: Partial<UpsertLanguageDto>) => {

    const documentTranslations = values.documentTranslations && values.documentTranslations.filter(document =>
        document.text !== "");
    let saveValues: Partial<UpsertLanguageDto> = {...values, documentTranslations};

    const {languageId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const handleError = () => {
      this.setState({
        errorMessage: getMessage("error.general")
      })
    };

    if (languageId) {
      await editLanguage(languageId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    } else {
      await addLanguage(saveValues, cancelTokenSource)
          .then(onSave)
          .catch(handleError)
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {language} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={language ? language : initialLanguageValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
                {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
                }
                <PraxFormGrid>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                      {getMessage("description")}
                    </Grid.Column>
                    <Grid.Column width={11}>
                      <Field
                          fluid
                          name="description"
                          component={Input}
                          validate={required}
                          autoFocus
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={5} verticalAlign="middle">
                    </Grid.Column>
                    <Grid.Column width={11} verticalAlign="middle">
                      <Field
                          name="basisLanguage"
                          component={CheckBox}
                          label={getMessage('language.basisLanguage')}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      {this.renderDocumentTranslations(form)}
                    </Grid.Column>
                  </Grid.Row>
                </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderDocumentTranslations(form: FormApi) {

    const {language, getMessage} = this.props;
    const documentTranslations = language ? language.documentTranslations : initialDocumentTranslations;

    const panes = documentTranslations.map((document, index) => {
      return (
          {
            menuItem: document.documentTypeId !== 0
                ? `${document.documentTypeId}. ${getMessage('language.reminder')}`
                : `${getMessage('bill')}`,
            render: () => <StyledTabPane attached={false}>
              <StyledField name={`documentTranslations[${index}].text`}
                           component={TextArea}
              />
              <Button
                  compact
                  type="button"
                  content='Clear'
                  icon='eraser'
                  labelPosition='left'
                  floated='right'
                  onClick={() => form.change(`documentTranslations[${index}].text`, "")}
              />
            </StyledTabPane>
          }
      );
    });

    return (
        <Tab menu={{secondary: true, pointing: true}} panes={panes}/>
    );
  }
}

export default withMessages(UpsertBillTextForm);
